import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Rocket, Users, Target, Megaphone, BookOpen,
  TrendingUp, CheckCircle, ArrowRight, Lightbulb, BarChart3,
  Share2, Mail, Zap, Award
} from 'lucide-react';

const playbooks = [
  {
    category: "Acquisition",
    icon: Users,
    color: "text-blue-600",
    bg: "bg-blue-50",
    strategies: [
      { title: "Agent Onboarding Referral Program", desc: "Integrate AREA referrals into new agent onboarding flows at brokerages. Offer dedicated landing pages for each brokerage partner.", priority: "high", impact: "High volume, steady pipeline" },
      { title: "Creator Partnership Outreach", desc: "Identify and reach out to real estate YouTubers, TikTokers, and newsletter authors. Offer Creator Partner rates (25%) and co-branded content.", priority: "high", impact: "Audience leverage" },
      { title: "Event & Conference Referrals", desc: "Sponsor or attend RE industry events. Provide QR-coded referral links for in-person networking.", priority: "medium", impact: "High-intent leads" },
      { title: "Edu Partner Program", desc: "Partner with RE schools and coaching programs to recommend AREA as a career resource. Each educator gets a tracked link.", priority: "medium", impact: "Long-term pipeline" },
    ]
  },
  {
    category: "Activation",
    icon: Zap,
    color: "text-violet-600",
    bg: "bg-violet-50",
    strategies: [
      { title: "Welcome Email Sequence", desc: "Send a 3-part drip campaign to new affiliates: welcome, first link setup, and first campaign ideas.", priority: "high", impact: "Partner activation rate" },
      { title: "Partner Onboarding Checklist", desc: "Guide new partners through terms acceptance, bank setup, link creation, and first share — tracked in their dashboard.", priority: "high", impact: "Reduce time-to-first-referral" },
      { title: "Content Kit for Partners", desc: "Provide ready-to-use social posts, email templates, and banner images that partners can customize and share.", priority: "medium", impact: "Lower barrier to promotion" },
    ]
  },
  {
    category: "Retention & Growth",
    icon: TrendingUp,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    strategies: [
      { title: "Monthly Partner Newsletter", desc: "Send monthly updates: new features, top earners leaderboard, program changes, and success stories.", priority: "high", impact: "Partner engagement" },
      { title: "Tier Upgrade Incentives", desc: "Offer temporary commission boosts (e.g., +5% for 30 days) when partners hit milestones (10 referrals, $500 earned).", priority: "medium", impact: "Motivate performance" },
      { title: "Leaderboard & Recognition", desc: "Highlight top 5 partners monthly. Send personalized congratulatory emails. Feature in community.", priority: "medium", impact: "Competitive motivation" },
      { title: "Seasonal Campaigns", desc: "Run Q1 kickoff, summer challenge, and year-end pushes with bonus commission windows.", priority: "low", impact: "Periodic revenue spikes" },
    ]
  },
  {
    category: "Analytics & Optimization",
    icon: BarChart3,
    color: "text-amber-600",
    bg: "bg-amber-50",
    strategies: [
      { title: "Conversion Funnel Analysis", desc: "Track drop-off between click → signup → paid conversion. Identify bottlenecks and A/B test landing pages.", priority: "high", impact: "Improve conversion rate" },
      { title: "Partner Performance Segmentation", desc: "Segment partners by type and performance. Focus support on high-potential, low-activity partners.", priority: "medium", impact: "Targeted growth" },
      { title: "Attribution Window Testing", desc: "Experiment with 30-day vs 60-day vs 90-day windows. Measure impact on partner satisfaction and conversion rates.", priority: "low", impact: "Data-driven optimization" },
    ]
  },
];

const priorityColors = {
  high: "bg-red-50 text-red-700",
  medium: "bg-amber-50 text-amber-700",
  low: "bg-gray-50 text-gray-600",
};

export default function AdminGrowthPlaybook() {
  const [activeCategory, setActiveCategory] = useState(0);

  return (
    <div className="max-w-6xl mx-auto" data-testid="growth-playbook-page">
      <div className="flex items-center gap-4 mb-8">
        <Link to="/admin"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Admin</Button></Link>
        <div>
          <h1 className="text-xl font-bold text-[#0f1f33]">Growth Playbook</h1>
          <p className="text-sm text-gray-400">Strategies to scale the affiliate program.</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center"><BookOpen className="w-3.5 h-3.5 text-blue-600" /></div>
            <span className="text-xs text-gray-400">Total Strategies</span>
          </div>
          <p className="text-lg font-bold text-[#0f1f33]">{playbooks.reduce((a, c) => a + c.strategies.length, 0)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center"><Target className="w-3.5 h-3.5 text-red-600" /></div>
            <span className="text-xs text-gray-400">High Priority</span>
          </div>
          <p className="text-lg font-bold text-[#0f1f33]">{playbooks.reduce((a, c) => a + c.strategies.filter(s => s.priority === 'high').length, 0)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center"><Rocket className="w-3.5 h-3.5 text-violet-600" /></div>
            <span className="text-xs text-gray-400">Categories</span>
          </div>
          <p className="text-lg font-bold text-[#0f1f33]">{playbooks.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center"><Lightbulb className="w-3.5 h-3.5 text-emerald-600" /></div>
            <span className="text-xs text-gray-400">Quick Wins</span>
          </div>
          <p className="text-lg font-bold text-[#0f1f33]">{playbooks.reduce((a, c) => a + c.strategies.filter(s => s.priority === 'high').length, 0)}</p>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-50 p-1 rounded-xl overflow-x-auto">
        {playbooks.map((pb, i) => (
          <button key={i} onClick={() => setActiveCategory(i)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${activeCategory === i ? 'bg-white text-[#0f1f33] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`playbook-tab-${i}`}>
            <pb.icon className="w-3.5 h-3.5" /> {pb.category}
          </button>
        ))}
      </div>

      {/* Strategies */}
      <div className="space-y-4">
        {playbooks[activeCategory].strategies.map((s, i) => (
          <Card key={i} className="border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-[#0f1f33]">{s.title}</h3>
                    <Badge className={`text-xs capitalize ${priorityColors[s.priority]}`}>{s.priority}</Badge>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed mb-3">{s.desc}</p>
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs text-gray-400">Impact: {s.impact}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
